/**
 * Artist Data Context
 *
 * Provides client-side caching for artist enrichment data.
 * Fetches from /api/v1/research/artist/:name on demand.
 * Prevents duplicate fetches and shares data across components.
 */

import { createContext, useContext, useCallback, useRef, useState, useEffect, ReactNode } from 'react';
import type { FullArtistData, MusicBrainzMember, MusicBrainzLinks } from '@tastymusic/research-agent';

// Extended artist data with resolved embeds
interface EnrichedArtistData extends FullArtistData {
  youtubeVideoId?: string;
  youtubeTitle?: string;
  bandcampEmbed?: {
    embedHtml: string;
    albumId?: string;
    trackId?: string;
    title?: string;
    bandcampUrl?: string;
  };
  // Resolution metadata - includes rejection flags
  _meta?: {
    cachedAt?: string;
    bandcampSource?: string;
    bandcampRejected?: boolean; // LLM explicitly rejected all Bandcamp results
    youtubeSource?: string;
    youtubeRejected?: boolean; // LLM explicitly rejected all YouTube results
  };
}

interface ArtistDataContextValue {
  getArtistData: (name: string) => EnrichedArtistData | null;
  fetchArtistData: (name: string, tracks?: string[]) => Promise<EnrichedArtistData | null>;
}

const ArtistDataContext = createContext<ArtistDataContextValue | null>(null);

// In-flight fetches to prevent duplicate requests
const inFlightFetches = new Map<string, Promise<EnrichedArtistData | null>>();

export function ArtistDataProvider({ children }: { children: ReactNode }) {
  // Cache of fetched artist data
  const cacheRef = useRef<Map<string, EnrichedArtistData | null>>(new Map());
  // Force re-render when cache updates
  const [, setVersion] = useState(0);

  const getArtistData = useCallback((name: string): EnrichedArtistData | null => {
    return cacheRef.current.get(name) ?? null;
  }, []);

  const fetchArtistData = useCallback(async (name: string, tracks?: string[]): Promise<EnrichedArtistData | null> => {
    // Return cached data if available
    if (cacheRef.current.has(name)) {
      return cacheRef.current.get(name) ?? null;
    }

    // Return in-flight fetch if already fetching
    if (inFlightFetches.has(name)) {
      return inFlightFetches.get(name)!;
    }

    // Start new fetch
    const fetchPromise = (async () => {
      try {
        // Include tracks for better YouTube search results (try each in order)
        const url = tracks && tracks.length > 0
          ? `/api/v1/research/artist/${encodeURIComponent(name)}?tracks=${encodeURIComponent(tracks.join(','))}`
          : `/api/v1/research/artist/${encodeURIComponent(name)}`;
        const response = await fetch(url);
        if (!response.ok) {
          console.error(`[artist-cache] Failed to fetch ${name}: ${response.status}`);
          cacheRef.current.set(name, null);
          return null;
        }

        const data = await response.json();
        if (!data.found) {
          cacheRef.current.set(name, null);
          return null;
        }

        const artistData: EnrichedArtistData = {
          gid: data.gid,
          name: data.name,
          type: data.type,
          area: data.area,
          links: data.links as MusicBrainzLinks,
          members: data.members as MusicBrainzMember[],
          memberOf: data.memberOf,
          // Include resolved embeds from API
          youtubeVideoId: data.youtubeVideoId,
          youtubeTitle: data.youtubeTitle,
          bandcampEmbed: data.bandcampEmbed,
          // Include metadata with rejection flags
          _meta: data._meta,
        };

        cacheRef.current.set(name, artistData);
        // Trigger re-render to update consumers
        setVersion(v => v + 1);
        return artistData;
      } catch (error) {
        console.error(`[artist-cache] Error fetching ${name}:`, error);
        cacheRef.current.set(name, null);
        return null;
      } finally {
        inFlightFetches.delete(name);
      }
    })();

    inFlightFetches.set(name, fetchPromise);
    return fetchPromise;
  }, []);

  return (
    <ArtistDataContext.Provider value={{ getArtistData, fetchArtistData }}>
      {children}
    </ArtistDataContext.Provider>
  );
}

export function useArtistData(name: string | undefined, tracks?: string[]) {
  const context = useContext(ArtistDataContext);
  const [data, setData] = useState<EnrichedArtistData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Stringify tracks for dependency comparison (avoid infinite re-renders)
  const tracksKey = tracks?.join(',') || '';

  // Use useEffect for side effects (fetching)
  useEffect(() => {
    // Skip if no name provided (caller has pre-enriched data)
    if (!name) {
      setData(null);
      setIsLoading(false);
      return;
    }
    if (!context) {
      console.warn('[useArtistData] No context available for', name);
      return;
    }
    console.log('[useArtistData] Fetching', name, tracks?.length ? `(tracks: ${tracks.slice(0, 2).join(', ')}...)` : '');

    // Check if already cached
    const cached = context.getArtistData(name);
    if (cached) {
      setData(cached);
      return;
    }

    // Fetch if not cached - pass tracks for better YouTube search (tries each in order)
    setIsLoading(true);
    context.fetchArtistData(name, tracks).then((result) => {
      setData(result);
      setIsLoading(false);
    });
  }, [name, tracksKey, context]);

  return { data, isLoading };
}

export { ArtistDataContext };
export type { EnrichedArtistData };
